import { useMemo } from 'react'

import type { SubscriptionPlan } from 'services/subscriptionApi'

import { useCompanySubscription, useSubscriptionPlans } from './useSubscription'

/**
 * The only two fields the resolution reads. Typed structurally rather than as
 * `CompanySubscription` so `BillingOverview` can pass its own narrower local
 * `BillingSubscription` — demanding the full entity would force a cast at a call site
 * that legitimately holds less than the whole thing.
 */
type PlanBearingSubscription = {
  plan?: SubscriptionPlan | null
  stripe_product_id?: string | null
}

/**
 * Resolve which catalog plan a subscription is on.
 *
 * Extracted from `BillingOverview` when the sidebar became the second caller
 * (working-conventions §36 — the second hand-roll is a shared component). The fallback
 * matters: a subscription does not always embed its plan, so it has to be matched back
 * to the catalog by Stripe product id. Two copies of that would drift the moment one
 * gained a case the other lacked.
 *
 * Takes the data rather than fetching it, because `BillingOverview` is handed a
 * subscription as a prop — the internal company-detail Plan tab renders it for a company
 * that is not the viewer's own.
 */
export const resolveCurrentPlan = (
  subscription: PlanBearingSubscription | null | undefined,
  plans: SubscriptionPlan[],
): SubscriptionPlan | null => {
  if (subscription?.plan) return subscription.plan

  const productId = subscription?.stripe_product_id
  if (productId) return plans.find((p) => p.stripe_product_id === productId) ?? null

  return null
}

/**
 * The plan the CURRENT company is on.
 *
 * `enabled` is not a convenience — it is required for any caller that renders on every
 * page. Internal Vera staff have no company subscription, so leaving this on would fire
 * a doomed request on every internal route. Callers gate on the viewer being a company
 * user; see `SidebarUserBlock`.
 */
export const useCurrentPlan = (enabled = true) => {
  const { data: subscription, isLoading: subscriptionLoading } = useCompanySubscription(
    null,
    enabled,
  )
  const { data: plans = [], isLoading: plansLoading } = useSubscriptionPlans(enabled)

  const plan = useMemo(
    () => resolveCurrentPlan(subscription, plans),
    [subscription, plans],
  )

  return { plan, isLoading: enabled && (subscriptionLoading || plansLoading) }
}
